import { useEffect, useState } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const CACHE_KEY = 'site_favicon_url';
const PACKAGE_CACHE_KEY = 'site_favicon_package';

const DynamicFavicon = () => {
  const [faviconUrl, setFaviconUrl] = useState(null);
  const [packageInstalled, setPackageInstalled] = useState(false);

  useEffect(() => {
    // Fetch settings immediately
    const fetchFavicon = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/site-settings`);
        const data = await res.json();
        
        // Check if favicon package is installed
        const hasPackage = data.favicon_package_installed === true;
        setPackageInstalled(hasPackage);
        
        // Cache package status
        try {
          localStorage.setItem(PACKAGE_CACHE_KEY, hasPackage ? 'true' : 'false');
        } catch (e) {}
        
        if (hasPackage) {
          // Package is installed, we'll use the API endpoints
          setFaviconUrl('package');
        } else {
          // Use single favicon or logo
          let url = data.favicon || data.logo_header;
          
          if (url) {
            // Add BACKEND_URL if it's a relative path
            if (url.startsWith('/')) {
              url = `${BACKEND_URL}${url}`;
            }
            
            // Cache for instant load next time
            try {
              localStorage.setItem(CACHE_KEY, url);
            } catch (e) {
              console.debug('Could not cache favicon URL');
            }
            
            setFaviconUrl(url);
          }
        }

        // Update page title if available
        if (data.logo_alt) {
          document.title = data.logo_alt;
        }
      } catch (err) {
        console.debug('Could not fetch site settings for favicon');
        // Try to use cached values
        try {
          const cachedPackage = localStorage.getItem(PACKAGE_CACHE_KEY);
          if (cachedPackage === 'true') {
            setPackageInstalled(true);
            setFaviconUrl('package');
          } else {
            const cached = localStorage.getItem(CACHE_KEY);
            if (cached) setFaviconUrl(cached);
          }
        } catch (e) {}
      }
    };

    fetchFavicon();
  }, []);

  useEffect(() => {
    if (!faviconUrl) return;

    // Remove all existing favicons first (clean slate)
    const existingIcons = document.querySelectorAll('link[rel*="icon"], link[rel="manifest"]');
    existingIcons.forEach(icon => {
      if (icon.id && icon.id.startsWith('dynamic-')) {
        // Keep dynamic ones, will update
      } else {
        icon.remove();
      }
    });

    if (faviconUrl === 'package') {
      // Using favicon package - add all the proper link tags
      const timestamp = Date.now();
      
      // Standard favicon (ico)
      let faviconIco = document.getElementById('dynamic-favicon-ico');
      if (!faviconIco) {
        faviconIco = document.createElement('link');
        faviconIco.id = 'dynamic-favicon-ico';
        faviconIco.rel = 'icon';
        faviconIco.type = 'image/x-icon';
        document.head.appendChild(faviconIco);
      }
      faviconIco.href = `${BACKEND_URL}/api/favicon.ico?v=${timestamp}`;
      
      // 16x16 favicon
      let favicon16 = document.getElementById('dynamic-favicon-16');
      if (!favicon16) {
        favicon16 = document.createElement('link');
        favicon16.id = 'dynamic-favicon-16';
        favicon16.rel = 'icon';
        favicon16.type = 'image/png';
        favicon16.sizes = '16x16';
        document.head.appendChild(favicon16);
      }
      favicon16.href = `${BACKEND_URL}/api/favicon/favicon-16x16.png?v=${timestamp}`;
      
      // 32x32 favicon
      let favicon32 = document.getElementById('dynamic-favicon-32');
      if (!favicon32) {
        favicon32 = document.createElement('link');
        favicon32.id = 'dynamic-favicon-32';
        favicon32.rel = 'icon';
        favicon32.type = 'image/png';
        favicon32.sizes = '32x32';
        document.head.appendChild(favicon32);
      }
      favicon32.href = `${BACKEND_URL}/api/favicon/favicon-32x32.png?v=${timestamp}`;
      
      // Apple touch icon
      let appleIcon = document.getElementById('dynamic-apple-icon');
      if (!appleIcon) {
        appleIcon = document.createElement('link');
        appleIcon.id = 'dynamic-apple-icon';
        appleIcon.rel = 'apple-touch-icon';
        appleIcon.sizes = '180x180';
        document.head.appendChild(appleIcon);
      }
      appleIcon.href = `${BACKEND_URL}/api/favicon/apple-touch-icon.png?v=${timestamp}`;
      
      // Web manifest
      let manifest = document.getElementById('dynamic-manifest');
      if (!manifest) {
        manifest = document.createElement('link');
        manifest.id = 'dynamic-manifest';
        manifest.rel = 'manifest';
        document.head.appendChild(manifest);
      }
      manifest.href = `${BACKEND_URL}/api/site.webmanifest?v=${timestamp}`;
      
      console.debug('Favicon package applied');
      
    } else {
      // Using single favicon URL
      const urlWithCache = `${faviconUrl}${faviconUrl.includes('?') ? '&' : '?'}v=${Date.now()}`;

      // Create new favicon link (primary)
      let favicon = document.getElementById('dynamic-favicon');
      if (!favicon) {
        favicon = document.createElement('link');
        favicon.id = 'dynamic-favicon';
        favicon.rel = 'icon';
        document.head.appendChild(favicon);
      }
      favicon.href = urlWithCache;

      // Create shortcut icon (for Safari/IE)
      let shortcutIcon = document.getElementById('dynamic-shortcut-icon');
      if (!shortcutIcon) {
        shortcutIcon = document.createElement('link');
        shortcutIcon.id = 'dynamic-shortcut-icon';
        shortcutIcon.rel = 'shortcut icon';
        document.head.appendChild(shortcutIcon);
      }
      shortcutIcon.href = urlWithCache;

      // Apple touch icon
      let appleIcon = document.getElementById('dynamic-apple-icon');
      if (!appleIcon) {
        appleIcon = document.createElement('link');
        appleIcon.id = 'dynamic-apple-icon';
        appleIcon.rel = 'apple-touch-icon';
        appleIcon.sizes = '180x180';
        document.head.appendChild(appleIcon);
      }
      appleIcon.href = urlWithCache;

      console.debug('Favicon updated to:', urlWithCache);
    }

    // For Safari - force refresh by modifying document title briefly
    const originalTitle = document.title;
    document.title = originalTitle + ' ';
    setTimeout(() => {
      document.title = originalTitle;
    }, 10);

  }, [faviconUrl]);

  return null;
};

export default DynamicFavicon;
